import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPolicyByNumber } from '../api/apiPolicy';
import '../assets/styles/pages/_help-me-decide.scss';

const HelpMeDecide = () => {
    const [policyId, setPolicyId] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const validatePolicyId = (value) => {
        // Alphanumeric, max 10 characters
        const alphanumericRegex = /^[a-zA-Z0-9]*$/;
        if (!alphanumericRegex.test(value)) {
            return 'Policy ID must contain only letters and numbers';
        }
        if (value.length > 10) {
            return 'Policy ID must be 10 characters or less';
        }
        return '';
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        const validationError = validatePolicyId(value);
        
        if (!validationError && value.length <= 10) {
            setPolicyId(value.toUpperCase());
            setError('');
        } else if (value.length === 0) {
            setPolicyId('');
            setError('');
        } else {
            setError(validationError);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!policyId || policyId.trim().length === 0) {
            setError('Please enter a Policy ID');
            return;
        }

        const validationError = validatePolicyId(policyId);
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsLoading(true);

        try {
            const response = await getPolicyByNumber(policyId);
            console.log('API Response:', response.status, response.data);
            
            // Check if response is 200 and policy number matches
            if (response.status === 200) {
                // According to requirements: response.body "policyListItems": "policyMini": "basicPolicy": "policyNumber"
                const responseData = response.data;
                
                // Try to find policy number in response
                // Structure: policyListItems[].policyMini.basicPolicy.policyNumber
                let foundPolicyNumber = null;
                
                // First check if policyListItems exists and is an array
                if (responseData.policyListItems && Array.isArray(responseData.policyListItems) && responseData.policyListItems.length > 0) {
                    // Look through policyListItems array
                    for (const item of responseData.policyListItems) {
                        if (item.policyMini && item.policyMini.basicPolicy && item.policyMini.basicPolicy.policyNumber) {
                            foundPolicyNumber = item.policyMini.basicPolicy.policyNumber;
                            break;
                        }
                    }
                }
                
                // Also check direct basicPolicy structure (fallback)
                if (!foundPolicyNumber && responseData.basicPolicy && responseData.basicPolicy.policyNumber) {
                    foundPolicyNumber = responseData.basicPolicy.policyNumber;
                }

                // Compare policy numbers (case insensitive)
                if (foundPolicyNumber && foundPolicyNumber.toUpperCase() === policyId.toUpperCase()) {
                    // Policy found in INow → redirect to New portal
                    navigate('/portal-redirect?portal=new');
                } else {
                    // Policy number doesn't match → redirect to Legacy portal
                    navigate('/portal-redirect?portal=legacy');
                }
            } else {
                // Non-200 status (404, 400, 403, 500, etc.) → redirect to Legacy portal
                console.log(`Non-200 status (${response.status}), redirecting to legacy portal`);
                navigate('/portal-redirect?portal=legacy');
            }
        } catch (err) {
            // Handle errors: 400, 404, 500, network errors, etc.
            // Default to Legacy portal if policy not found
            console.error('Policy API Error:', err);
            if (err.response) {
                // API returned an error response
                // 400, 403, 404, 500 all mean policy not found, access denied, or invalid → Legacy portal
                const status = err.response.status;
                if (status === 400 || status === 403 || status === 404 || status === 500) {
                    // Policy not found, access denied, or server error → Legacy portal
                    console.log(`API error ${status}, redirecting to legacy portal`);
                    navigate('/portal-redirect?portal=legacy');
                } else {
                    setError('An error occurred. Please try again.');
                    setIsLoading(false);
                }
            } else {
                // Network error or other issues (like ERR_EMPTY_RESPONSE)
                // Default to Legacy portal for network errors too
                console.error('Network error, defaulting to legacy portal');
                navigate('/portal-redirect?portal=legacy');
            }
        }
    };

    return (
        <div className="help-me-decide-page">
            <div className="container">
                <div className="help-me-decide-content">
                    <h1 className="help-me-decide-title">Find Portal</h1>
                    
                    <form onSubmit={handleSubmit} className="help-me-decide-form">
                        <div className={`form-group ${error ? 'error' : ''}`}>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Enter Policy Id"
                                value={policyId}
                                onChange={handleInputChange}
                                maxLength={10}
                                disabled={isLoading}
                            />
                            {error && <div className="input-error">{error}</div>}
                        </div>

                        <button
                            type="submit"
                            className={`button-gradient help-me-decide-submit ${isLoading ? 'is-loading' : ''} ${!policyId || error ? 'disabled' : ''}`}
                            disabled={isLoading || !policyId || !!error}
                        >
                            <span>Submit</span>
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default HelpMeDecide;

